import {useEffect, useMemo, useState} from 'react'
import {Address, Option, Place} from '@helpers/addresses/models'
import useDebounce from '@hooks/useDebounce'
import useQuery from '@packages/apollo-hooks/lib/useApolloQuery'
import uniqBy from 'lodash/uniqBy'
import usePrevious from 'use-previous'

import {searchPlacesQuery} from '../queries'

const mapAddressToPlace = (address: Address): Place => {
  if (!address) return null
  return {
    _id: address.placeId,
    text: address.address,
    secondaryText: address.addressSecondary,
    location: address.location
  }
}

const mapPlaceToOption = (place: Place): Option => {
  if (!place) return null
  return {
    label: `${place.text}, ${place.secondaryText}`,
    value: place._id
  }
}

const useAddressOptions = ({client, websiteId, userId, deviceId, address, mapSessionToken}) => {
  const previousUserId = usePrevious(userId)
  const [filter, setFilter] = useState<string | null>(null)
  const [debouncedFilter] = useDebounce(filter, 500)
  const [selectedCity, setSelectedCity] = useState<string | null>(null)

  const {
    addresses: queryAddresses,
    loading: loadingAddresses,
    refetch: refetchAddresses
  } = useQuery({
    query: searchPlacesQuery,
    variables: {
      filter: '',
      websiteId,
      userId,
      countryCodes: [],
      sessionTokenGoogleMaps: mapSessionToken,
      unrestrictedSearch: true
    },
    fetchPolicy: 'network-only',
    omit: !websiteId,
    partial: true
  })

  const {addresses: suggestions, loading: loadingSuggestions} = useQuery({
    query: searchPlacesQuery,
    variables: {
      filter: `${debouncedFilter}${selectedCity ? `, ${selectedCity}` : ''}`,
      websiteId,
      userId,
      countryCodes: [],
      sessionTokenGoogleMaps: mapSessionToken,
      unrestrictedSearch: true
    },
    omit: !websiteId || !debouncedFilter,
    partial: true
  })

  useEffect(() => {
    // If the user just logged in, load it's addresses
    if (!previousUserId && userId) {
      refetchAddresses()
    }
  }, [refetchAddresses, client, deviceId, previousUserId, userId, websiteId])

  const addressOptions = useMemo(() => {
    const addresses = queryAddresses?.map(address => ({
      ...address,
      placeId: address._id
    }))

    if (!addresses?.length) return []

    const placeFromAddress = mapAddressToPlace(address)
    const allOptions = placeFromAddress ? [placeFromAddress, ...addresses] : addresses
    return allOptions.map(mapPlaceToOption)
  }, [address, queryAddresses])

  const suggestedOptions = useMemo(() => {
    const suggestedPlaces = suggestions
    if (!suggestedPlaces?.length) return []
    return suggestedPlaces.map(mapPlaceToOption)
  }, [suggestions])

  const options = useMemo(() => {
    const filteredAddressOptions = addressOptions.filter(option =>
      !filter ? true : option.label.toLowerCase().includes(filter.toLowerCase())
    )

    return uniqBy([...filteredAddressOptions, ...suggestedOptions], 'value')
  }, [addressOptions, suggestedOptions, filter])

  return {
    options,
    isLoadingOptions: loadingAddresses || loadingSuggestions,
    selectedCity,
    setSelectedCity,
    filter,
    setFilter
  }
}

export default useAddressOptions
